import i18n from '@/i18n/i18n'
import store from '@/store/store.js'

import {
  isNewPackGoBetaFeatureEnabled,
  isPackGoFeatureEnabled,
  isPrintClientAvailable,
  arePrintClientAndPrintersAvailable,
  isSwitchedToNewPackGo,
} from '@/app/packgo/shared/utilities/utilities.js'

import ClientHelperService from '@/services/client-helper.service.js'

import ToastService from '@/services/toast.service'

import { SHIPPING_INCOMING_ORDERS_ROUTE } from '@/router/routes/shipping.routes'
import {
  NEW_PACKGO_QUEUE_ROUTE,
  PACKGO_CONNECT_ROUTE,
  PACKGO_INTRO_ROUTE,
  PACKGO_QUEUE_ROUTE,
  NEW_PACKGO_BROWSER_NOT_SUPPORTED,
  NEW_PACKGO_PRINT_CLIENT_NOT_INSTALLED,
} from '@/router/routes/packgo.routes.names.js'
import { usePackGoSettingsStore } from '@/store/packgo-settings'

/**
 * Check if user has access to Pack & Go
 * @param {import('node_modules/vue-router/types/index').Route} _to
 * @param {import('node_modules/vue-router/types/index').Route} _from
 * @param {import('node_modules/vue-router/types/index').NavigationGuardNext} next
 */
export async function packGoRouteGuard(_to, _from, next) {
  if (!isPackGoFeatureEnabled()) {
    ToastService.error(
      i18n.t(
        'Pack & Go is not available in your current plan. See our plans page for more information on which features are available with your current subscription.',
      ),
      {
        autoDismiss: false,
      },
    )

    next({ name: SHIPPING_INCOMING_ORDERS_ROUTE })
  } else {
    next()
  }
}

/**
 * Checks if user selected scanner or automatic mode
 */
const isModeSet = async () => {
  const settingsStore = usePackGoSettingsStore()
  await settingsStore.fetchOrderQueueSettings()
  return settingsStore.isModeSet
}

/**
 * Check if user has access to Pack & Go Intro
 * @param {import('node_modules/vue-router/types/index').Route} _to
 * @param {import('node_modules/vue-router/types/index').Route} _from
 * @param {import('node_modules/vue-router/types/index').NavigationGuardNext} next
 */
export async function packGoIntroRouteGuard(_to, _from, next) {
  if (await isModeSet()) {
    next({ name: PACKGO_CONNECT_ROUTE })
  } else {
    next()
  }
}

/**
 * Check if user has access to Pack & Go Connect
 * @param {import('node_modules/vue-router/types/index').Route} _to
 * @param {import('node_modules/vue-router/types/index').Route} _from
 * @param {import('node_modules/vue-router/types/index').NavigationGuardNext} next
 */
export async function packGoConnectRouteGuard(_to, _from, next) {
  if (!(await isModeSet())) {
    next({ name: PACKGO_INTRO_ROUTE })
  } else if (await arePrintClientAndPrintersAvailable()) {
    next({ name: PACKGO_QUEUE_ROUTE })
  } else {
    next()
  }
}

/**
 * Check if user has access to Pack & Go Queue
 * @param {import('node_modules/vue-router/types/index').Route} _to
 * @param {import('node_modules/vue-router/types/index').Route} _from
 * @param {import('node_modules/vue-router/types/index').NavigationGuardNext} next
 */
export async function packGoQueueRouteGuard(_to, _from, next) {
  if (!(await isModeSet())) {
    next({ name: PACKGO_INTRO_ROUTE })
  } else if (!(await arePrintClientAndPrintersAvailable())) {
    next({ name: PACKGO_CONNECT_ROUTE })
  } else if (isSwitchedToNewPackGo() && (isNewPackGoBetaFeatureEnabled() || store.getters.isStaffUser)) {
    next({ name: NEW_PACKGO_QUEUE_ROUTE })
  } else {
    next()
  }
}

/**
 * Check if user has access to the new Pack & Go UI
 * @param {import('node_modules/vue-router/types/index').Route} _to
 * @param {import('node_modules/vue-router/types/index').Route} _from
 * @param {import('node_modules/vue-router/types/index').NavigationGuardNext} next
 */

export async function newPackGoRouteGuard(_to, _from, next) {
  if (isPackGoFeatureEnabled()) {
    if (!(isNewPackGoBetaFeatureEnabled() || store.getters.isStaffUser)) {
      next({ name: PACKGO_INTRO_ROUTE })
    } else {
      next()
    }
  } else {
    next({ name: SHIPPING_INCOMING_ORDERS_ROUTE })
  }
}

/**
 * Check if user has access to new Pack & Go Intro
 * @param {import('node_modules/vue-router/types/index').Route} _to
 * @param {import('node_modules/vue-router/types/index').Route} _from
 * @param {import('node_modules/vue-router/types/index').NavigationGuardNext} next
 */
export async function newPackGoIntroRouteGuard(_to, _from, next) {
  // As the onboarding flow has not been implemented yet in the new P&G UI, always route to the current implementation
  next({ name: PACKGO_INTRO_ROUTE })
}

// Commented out guards related to the new Pack&Go intro/welcome, intro/settings and intro/download
// as the onboarding is not ready
// Don't forget to import the routes when un-commenting

// /**
//  * Check if user has access to new Pack & Go Welcome
//  * @param {import('node_modules/vue-router/types/index').Route} _to
//  * @param {import('node_modules/vue-router/types/index').Route} _from
//  * @param {import('node_modules/vue-router/types/index').NavigationGuardNext} next
//  */
// export async function newPackGoIntroWelcomeRouteGuard(_to, _from, next) {
//   if (await isModeSet()) {
//     next({ name: NEW_PACKGO_INTRO_DOWNLOAD_ROUTE })
//   } else {
//     next()
//   }
// }

// /**
//  * Check if user has access to new Pack & Go intro/settings or intro/download
//  * @param {import('node_modules/vue-router/types/index').Route} to
//  * @param {import('node_modules/vue-router/types/index').Route} _from
//  * @param {import('node_modules/vue-router/types/index').NavigationGuardNext} next
//  */
// export async function newPackGoDownloadOrSettingsRouteGuard(to, _from, next) {
//   if (!(await isModeSet())) {
//     if (to.name === NEW_PACKGO_INTRO_SETTINGS_ROUTE) {
//       next()
//     } else {
//       next({ name: NEW_PACKGO_INTRO_WELCOME_ROUTE })
//     }
//   } else {
//     if (await arePrintClientAndPrintersAvailable()) {
//       next({ name: NEW_PACKGO_QUEUE_ROUTE })
//     } else if (to.name === NEW_PACKGO_INTRO_SETTINGS_ROUTE) {
//       next({ name: NEW_PACKGO_INTRO_DOWNLOAD_ROUTE })
//     } else {
//       next()
//     }
//   }
// }

/**
 * Check if user has access to new Pack & Go Queue
 * @param {import('node_modules/vue-router/types/index').Route} _to
 * @param {import('node_modules/vue-router/types/index').Route} _from
 * @param {import('node_modules/vue-router/types/index').NavigationGuardNext} next
 */
export async function newPackGoQueueRouteGuard(_to, _from, next) {
  if (!ClientHelperService.isBrowserSupported()) {
    next({ name: NEW_PACKGO_BROWSER_NOT_SUPPORTED })
  } else if (!(await isModeSet())) {
    next({ name: PACKGO_INTRO_ROUTE })
  } else if (!(await isPrintClientAvailable())) {
    next({ name: NEW_PACKGO_PRINT_CLIENT_NOT_INSTALLED })
  } else {
    next()
  }
}

/**
 * Check if user's browser is supported
 * @param {import('node_modules/vue-router/types/index').Route} _to
 * @param {import('node_modules/vue-router/types/index').Route} _from
 * @param {import('node_modules/vue-router/types/index').NavigationGuardNext} next
 */
export async function newPackGoBrowserNotSupportedRouteGuard(_to, _from, next) {
  if (ClientHelperService.isBrowserSupported()) {
    next({ name: NEW_PACKGO_QUEUE_ROUTE })
  } else {
    next()
  }
}

/**
 * Check if user's browser is supported
 * @param {import('node_modules/vue-router/types/index').Route} _to
 * @param {import('node_modules/vue-router/types/index').Route} _from
 * @param {import('node_modules/vue-router/types/index').NavigationGuardNext} next
 */
export async function newPackGoPrintClientNotInstalledRouteGuard(_to, _from, next) {
  if (await isPrintClientAvailable()) {
    next({ name: NEW_PACKGO_QUEUE_ROUTE })
  } else {
    next()
  }
}
